import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import SettingsIcon from "@mui/icons-material/Settings";
import {
  ClickAwayListener,
  Fade,
  FormGroup,
  IconButton,
  Popper,
  Switch,
  Tooltip,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../hooks";
import {
  setIfHyperlink,
  setIsIframeAllowed,
  setUsingLang,
} from "../../store/reducers/settings";
import { loadState, saveState } from "../../utils/localStorage.utils";
import { LocalStorageKeys } from "../../utils/strings.utils";
import {
  Selector,
  SelectorOption,
  SettingAction,
  SettingPaper,
  SettingUnit,
  SettingUnitTitle,
} from "./index.styles";
import { useIdentity } from "../../hooks/useIdentity";
import { useWhiteboardRoom } from "../../hooks/useWhiteboardRoom";
import { createClickEngagementRecorder } from "../../utils/api";

const Setting = () => {
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation();
  const [lang, setLang] = useState(i18n.language);
  const changeLanguage = async (ln: any) => {
    await i18n.changeLanguage(ln);
    setLang(await ln);
    dispatch(setUsingLang(await ln));
  };
  const [isOpen, setIsOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { ifHyperlink, isIframeAllowed } = useAppSelector(
    (state) => state.settings
  );
  const { roomDomain } = useWhiteboardRoom();
  const clickRecorder = createClickEngagementRecorder(roomDomain, roomDomain);

  useEffect(() => {
    try {
      const isIframeAllowedLocal =
        loadState(LocalStorageKeys.IS_IFRAME_ALLOWED) ?? true; // default true
      saveState(LocalStorageKeys.IS_IFRAME_ALLOWED, isIframeAllowedLocal);
      dispatch(setIsIframeAllowed(isIframeAllowedLocal));
    } catch ({ message }) {
      console.error(`Can't get local storage, reason: (${message})`);
    }
  }, []);

  const handleSettingBtnClick = (e: React.MouseEvent<HTMLElement>) => {
    clickRecorder("menu-settings");
    setAnchorEl(e.currentTarget);
    setIsOpen((prev) => !prev);
  };
  const handleHyperlinkChange = async (event: any) => {
    const checked = event.target.checked;
    dispatch(setIfHyperlink(checked));
    try {
      saveState(LocalStorageKeys.IF_HYPERLINK, checked);
    } catch ({ message }) {
      console.error(`Can't set local storage, reason: (${message})`);
    }
  };
  const handleIframeChange = async (event: any) => {
    const checked = event.target.checked;
    dispatch(setIsIframeAllowed(checked));
    try {
      saveState(LocalStorageKeys.IS_IFRAME_ALLOWED, checked);
      // handle setting from extension
      window.dispatchEvent(new Event("setIsIframeAllowed"));
      // handle setting from iframe
      if (window.parent) {
        window.parent.postMessage(
          { protico: { isIframeAllowed: checked } },
          "*"
        );
      }
    } catch ({ message }) {
      //console.error(`Can't set local storage, reason: (${message})`);
    }
  };
  const { secondFontColor } = useAppSelector((state) => state.layout);
  const { isFromExtension } = useIdentity();

  return (
    <ClickAwayListener onClickAway={() => setIsOpen(false)}>
      <div>
        <Tooltip
          title={t("Menu_Settings")}
          placement="left-end"
          arrow
          disableInteractive
        >
          <IconButton aria-label="invite" onClick={handleSettingBtnClick}>
            <SettingsIcon color="action" fontSize="small" />
          </IconButton>
        </Tooltip>
        <Popper
          open={isOpen}
          anchorEl={anchorEl}
          placement="left-start"
          transition
          modifiers={[{ name: "arrow", enabled: true }]}
          style={{ zIndex: 999 }}
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={350}>
              <SettingPaper>
                <FormGroup>
                  <SettingUnit>
                    <SettingUnitTitle>
                      {t("Setting_Hyperlink")}
                      <Tooltip title={t("Setting_Hyperlink_Helptext")}>
                        <HelpOutlineIcon
                          sx={{
                            fontSize: "12px",
                          }}
                        />
                      </Tooltip>
                    </SettingUnitTitle>
                    <SettingAction>
                      <Switch
                        checked={ifHyperlink}
                        onChange={handleHyperlinkChange}
                        name="hyperlink"
                        size="small"
                      />
                    </SettingAction>
                  </SettingUnit>
                  {isFromExtension && (
                    <SettingUnit>
                      <SettingUnitTitle>
                        {t("Setting_Iframe")}
                        <Tooltip title={t("Setting_Iframe_Helptext")}>
                          <HelpOutlineIcon
                            sx={{
                              fontSize: "12px",
                            }}
                          />
                        </Tooltip>
                      </SettingUnitTitle>
                      <SettingAction>
                        <Switch
                          checked={isIframeAllowed}
                          onChange={handleIframeChange}
                          name="iframe"
                          size="small"
                        />
                      </SettingAction>
                    </SettingUnit>
                  )}
                  <SettingUnit>
                    <SettingUnitTitle>{t("Setting_Language")}</SettingUnitTitle>
                    <SettingAction>
                      <Selector
                        ifSecondFontColor={secondFontColor}
                        style={{ marginLeft: "auto" }}
                        id="ln-selector"
                        variant="standard"
                        value={lang}
                        onChange={async (e) => {
                          changeLanguage(e.target.value);
                        }}
                        MenuProps={{
                          disablePortal: true,
                          anchorReference: "anchorPosition",
                          anchorPosition: {
                            top: 75,
                            left: 200,
                          },
                          style: {
                            width: "150px",
                            height: "220px",
                          },
                        }}
                        sx={{
                          fontSize: "12px",
                        }}
                      >
                        <SelectorOption value={"en"}>🇬🇧 EN</SelectorOption>
                        <SelectorOption value={"zh-TW"}>🇹🇼 TW</SelectorOption>
                      </Selector>
                    </SettingAction>
                  </SettingUnit>
                </FormGroup>
              </SettingPaper>
            </Fade>
          )}
        </Popper>
      </div>
    </ClickAwayListener>
  );
};

export default Setting;
